import React, { createContext, useContext, useMemo } from 'react';
import { useQuery } from 'react-query';
import { ACCESS_BACKEND_URL } from '../configs';
import { useAxios } from './axios';

const DashboardProfileContext = createContext(undefined);

const defaultProfileShape = {
    email: 'email',
    name: 'name user',
    profile: 'Please add one',
    team: 'Please add one',
    role: 'user',
    manageremail: 'Required for access request',
};

const DASHBOARD_PROFILE_QUERY_KEY = ['access', 'profile', 'dashboard'];

/**
 * Loads access profile once per app session (react-query dedupes remounts / Strict Mode).
 */
export function DashboardProfileProvider({ children }) {
    const authAxios = useAxios();

    const { data, isLoading, isError, error } = useQuery(
        DASHBOARD_PROFILE_QUERY_KEY,
        async () => {
            const { data: body } = await authAxios.request({
                method: 'GET',
                url: `${ACCESS_BACKEND_URL}/api/profile?type=profile`,
            });
            return body;
        },
        {
            staleTime: Infinity,
            cacheTime: 1000 * 60 * 60,
            retry: false,
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
        }
    );

    const value = useMemo(
        () => ({
            profile: data ?? null,
            loading: isLoading,
            error: isError ? error : null,
            displayProfile: data ?? defaultProfileShape,
        }),
        [data, isLoading, isError, error]
    );

    return (
        <DashboardProfileContext.Provider value={value}>
            {children}
        </DashboardProfileContext.Provider>
    );
}

export function useDashboardProfile() {
    const ctx = useContext(DashboardProfileContext);
    if (ctx === undefined) {
        throw new Error(
            'useDashboardProfile must be used within DashboardProfileProvider'
        );
    }
    return ctx;
}

export function useOptionalDashboardProfile() {
    return useContext(DashboardProfileContext);
}
